import * as THREE from 'three';
import {
  CUT_PROJECTION_MIN_RATIO,
  DECAL_CUT_ARC_TO_LEN,
  DECAL_CUT_COLOR,
  DECAL_CUT_LEAF_WIDTH_M,
  DECAL_CUT_LEN_DEFAULT_M,
  DECAL_CUT_LEN_MAX_M,
  DECAL_CUT_LEN_MIN_M,
  DECAL_FILO_LEAF_LEN_M,
  DECAL_FILO_LEAF_WIDTH_M,
  DECAL_LEAF_CURVE_SEGMENTS,
  DECAL_NAME,
  DECAL_OPACITY,
  DECAL_QUALITY_COLORS,
  DECAL_SURFACE_OFFSET_M,
  DECAL_THRUST_DOT_COLOR,
  DECAL_THRUST_DOT_RADIUS_M,
  DECAL_THRUST_RING_INNER_M,
  DECAL_THRUST_RING_OUTER_M,
  DECAL_TTL_MS,
  EDGE_QUALITY,
  type EdgeQuality,
} from '../config/constants';
import { frontalBasis, projectToFrontalPlane } from '../hit-detection/frontalProjection';

/**
 * One live hit marker, ticked from the XR frame loop via `updateDecals`.
 * The fade/removal must NOT use `window.requestAnimationFrame`: inside an immersive
 * WebXR session (e.g. Meta Quest) that callback is paused, so markers would never
 * expire. Driving them from the renderer's animation loop works in VR and on desktop.
 */
type ActiveDecal = {
  group: THREE.Group;
  worldPoint: THREE.Vector3;
  camera: THREE.Object3D;
  materials: THREE.MeshBasicMaterial[];
  baseOpacities: number[];
  start: number;
  ttlMs: number; // Infinity => persists until clearDecals
};

const activeDecals: ActiveDecal[] = [];

/**
 * Spawns a slash-shaped hit marker at the contact point: a white leaf along the cut
 * trajectory (the *movement*) wrapped in a quality-coloured rim, plus a smaller
 * quality-coloured leaf along the blade edge (the real *filo*) drawn in front. For a
 * clean cut the filo leaf nests inside the white slash; for a piattonata it crosses it
 * at an angle. The white slash length scales with `cutLengthM` (the swing arc).
 *
 * The marker lives in the *viewer-facing vertical plane* (world up = vertical) and
 * billboards toward the camera every frame (see `updateDecals`), so a fendente always
 * reads vertical and a sgualembro diagonal — independent of where on the target it
 * landed. The leaf angles are computed once at impact and stay rigid as it re-faces the user.
 *
 * Auto-removes after `ttlMs` (default 500) once `updateDecals` is ticked each frame.
 * Pass `Infinity` to keep it until manually cleared via `clearDecals`.
 */
export function spawnHitDecal(
  parent: THREE.Object3D,
  worldPoint: THREE.Vector3,
  quality: EdgeQuality,
  camera: THREE.Object3D,
  edgeDir: THREE.Vector3,
  cutDir: THREE.Vector3 | null,
  ttlMs: number = DECAL_TTL_MS,
  cutLengthM: number | null = null,
): void {
  const group = new THREE.Group();
  group.name = DECAL_NAME;

  const meshes: THREE.Mesh[] = [];

  if (quality === EDGE_QUALITY.thrust) {
    const ring = makeRing(
      DECAL_THRUST_RING_INNER_M,
      DECAL_THRUST_RING_OUTER_M,
      DECAL_QUALITY_COLORS[quality],
    );
    group.add(ring);
    meshes.push(ring);

    const dot = makeDisc(DECAL_THRUST_DOT_RADIUS_M, DECAL_THRUST_DOT_COLOR);
    group.add(dot);
    meshes.push(dot);
  } else {
    const viewerPos = camera.getWorldPosition(new THREE.Vector3());
    const color = DECAL_QUALITY_COLORS[quality];

    // White slash (the movement), aligned to the cut trajectory. Drawn only when the
    // trajectory has a meaningful projection onto the viewer plane (a cut mostly
    // toward/away from the viewer has no defined angle). Length scales with the swing arc.
    const cutProj = cutDir ? projectToFrontalPlane(cutDir, worldPoint, viewerPos) : null;
    if (cutProj && cutProj.ratio >= CUT_PROJECTION_MIN_RATIO) {
      const slashLen =
        cutLengthM != null
          ? THREE.MathUtils.clamp(
              cutLengthM * DECAL_CUT_ARC_TO_LEN,
              DECAL_CUT_LEN_MIN_M,
              DECAL_CUT_LEN_MAX_M,
            )
          : DECAL_CUT_LEN_DEFAULT_M;

      const slash = makeLeaf(slashLen, DECAL_CUT_LEAF_WIDTH_M, DECAL_CUT_COLOR);
      slash.rotation.z = cutProj.barRad;
      slash.renderOrder = 1;
      group.add(slash);
      meshes.push(slash);
    }

    // Filo leaf (the real edge) in front of the slash: nests inside it on a clean cut,
    // crosses it at an angle on a piattonata. Always drawn — even when the trajectory
    // has no usable projection, the filo direction still reads.
    const edgeProj = projectToFrontalPlane(edgeDir, worldPoint, viewerPos);
    const filo = makeLeaf(DECAL_FILO_LEAF_LEN_M, DECAL_FILO_LEAF_WIDTH_M, color);
    filo.rotation.z = edgeProj?.barRad ?? Math.PI / 2; // fallback: vertical
    filo.renderOrder = 2;
    group.add(filo);
    meshes.push(filo);
  }

  orientToViewer(group, worldPoint, camera);
  parent.add(group);

  const materials = meshes.map((m) => m.material as THREE.MeshBasicMaterial);
  activeDecals.push({
    group,
    worldPoint: worldPoint.clone(),
    camera,
    materials,
    baseOpacities: materials.map((m) => m.opacity),
    start: performance.now(),
    ttlMs: Number.isFinite(ttlMs) && ttlMs > 0 ? ttlMs : Infinity,
  });
}

/**
 * Per-frame update for all live hit markers: re-billboard toward the viewer, fade by TTL,
 * and remove expired ones. MUST be called from the renderer's animation loop
 * (`renderer.setAnimationLoop`) so it ticks inside immersive WebXR sessions too.
 */
export function updateDecals(nowMs: number = performance.now()): void {
  for (let i = activeDecals.length - 1; i >= 0; i--) {
    const d = activeDecals[i]!;
    // Externally removed (e.g. clearDecals): drop from the registry.
    if (!d.group.parent) {
      activeDecals.splice(i, 1);
      continue;
    }
    orientToViewer(d.group, d.worldPoint, d.camera);
    if (!Number.isFinite(d.ttlMs)) continue;

    const t = (nowMs - d.start) / d.ttlMs;
    if (t >= 1) {
      disposeGroup(d.group);
      d.group.parent.remove(d.group);
      activeDecals.splice(i, 1);
      continue;
    }
    for (let j = 0; j < d.materials.length; j++) {
      d.materials[j]!.opacity = d.baseOpacities[j]! * (1 - t);
    }
  }
}

/** Removes every hit marker in `parent.children` and disposes their GPU resources. */
export function clearDecals(parent: THREE.Object3D): void {
  const toRemove: THREE.Object3D[] = [];
  for (const child of parent.children) {
    if (child.name === DECAL_NAME) toRemove.push(child);
  }
  for (const obj of toRemove) {
    disposeGroup(obj);
    parent.remove(obj);
  }
  // updateDecals prunes the now-parentless entries from the registry on its next tick.
}

const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _normal = new THREE.Vector3();
const _basis = new THREE.Matrix4();

/**
 * Position the marker at the contact point (nudged toward the viewer to avoid
 * z-fighting) and orient its local frame to the viewer-facing vertical plane:
 * local X = viewer's right, Y = world up, Z = toward viewer.
 */
function orientToViewer(
  group: THREE.Group,
  worldPoint: THREE.Vector3,
  camera: THREE.Object3D,
): void {
  const viewerPos = camera.getWorldPosition(new THREE.Vector3());
  frontalBasis(worldPoint, viewerPos, _right, _up, _normal);
  _basis.makeBasis(_right, _up, _normal);
  group.quaternion.setFromRotationMatrix(_basis);
  group.position.copy(worldPoint).addScaledVector(_normal, DECAL_SURFACE_OFFSET_M);
}

/**
 * A pointed leaf/vesica centered at the origin, lying in the XY plane and spanning
 * `length` along +X with `width` full thickness at its belly. Built from two quadratic
 * curves so the tips stay sharp (fruit-slash look). Scale/orient it via the mesh.
 */
function makeLeaf(length: number, width: number, color: number): THREE.Mesh {
  const hx = length / 2;
  const shape = new THREE.Shape();
  shape.moveTo(-hx, 0);
  shape.quadraticCurveTo(0, width, hx, 0); // top belly peaks at +width/2
  shape.quadraticCurveTo(0, -width, -hx, 0); // bottom belly at -width/2
  const geom = new THREE.ShapeGeometry(shape, DECAL_LEAF_CURVE_SEGMENTS);
  return new THREE.Mesh(geom, decalMaterial(color));
}

function makeRing(innerR: number, outerR: number, color: number): THREE.Mesh {
  const geom = new THREE.RingGeometry(innerR, outerR, 32);
  return new THREE.Mesh(geom, decalMaterial(color));
}

function makeDisc(radius: number, color: number): THREE.Mesh {
  const geom = new THREE.CircleGeometry(radius, 24);
  return new THREE.Mesh(geom, decalMaterial(color));
}

function decalMaterial(color: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: DECAL_OPACITY,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function disposeGroup(obj: THREE.Object3D): void {
  obj.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose());
    else mat.dispose();
  });
}
